/** ******************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2017, 2025
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} * */

import React, { useState, useMemo, useEffect } from 'react';
import { Button, TextInput } from '@carbon/react';
import { View, ViewOff, Copy } from '@carbon/react/icons';
import { useTranslation } from 'react-i18next';
import Invoker from '../utils/invoker';
import { getOauthErrorMessage, getMixedContentMessage } from '../utils/error-message-handler';

function TestToolAccessToken({ config, onChange }) {
  const [authToken, setAuthToken] = useState({ access_token: '' });
  const [authError, setAuthError] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const invoker = useMemo(() => new Invoker(), []);
  const { t } = useTranslation();

  useEffect(() => {
    if (!authToken.access_token) {
      setAuthToken({ access_token: '' });
    }
  }, [authToken.access_token]);

  // eslint-disable-next-line no-unused-vars
  const getToken = async (event) => {
    const authToken = {
      access_token: ''
    };

    // mixed content check - i.e. calling http from https - not allowed.
    if (window.location.protocol === 'https:' && config.flags.selectedTokenUrl.lastIndexOf(window.location.protocol) !== 0) {
      setAuthToken(authToken);
      setAuthError(getMixedContentMessage(t));
      return;
    }

    if (config.flags.oauthFlow === 'implicit') {
      // authorize calls call window.open, we can't get a handle on the response so just call it.
      invoker.authorize(config.flags, config.identification.clientID, config.scopes, config.identification.redirectUri);
    } else {
      try {
        const response = await invoker.getToken(
          config.flags,
          config.authToken,
          config.identification.clientID,
          config.identification.clientSecret,
          config.scopes,
          config.identification.redirectUri,
          config.userCredentials.username,
          config.userCredentials.password
        );

        setAuthError({});
        Object.keys(response).forEach((key) => {
          authToken[key] = response[key];
        });
      } catch (err) {
        setAuthError(await getOauthErrorMessage(err, t));
      } finally {
        setAuthToken(authToken);
        if (onChange && typeof onChange === 'function') {
          onChange(authToken);
        }
      }
    }
  };

  const updateToken = (event) => {
    const newToken = {};
    newToken.access_token = event.target.value;
    setAuthToken(newToken);

    if (onChange && typeof onChange === 'function') {
      onChange(newToken);
    }
  };

  let helpText = '';
  if (config.removeFields) {
    helpText =
      config.flags.oauthFlow === 'accessCode' || config.flags.oauthFlow === 'authorizationCode' ? t('explorer_help_access_code') : t('explorer_help_implicit');
  }

  return (
    <>
      <div className="getToken">
        {!config.removeFields && (
          <Button className="getTokenButton" onClick={getToken}>
            {t('explorer_get_token')}
          </Button>
        )}
        <div className="tokenFieldWrapper">
          <TextInput
            className="tokenField"
            labelText={`${t('explorer_access_token')} (${t('required')})`}
            type={showPassword ? 'text' : 'password'}
            id="tokenField"
            value={authToken.access_token}
            onChange={updateToken}
            helperText={helpText}
          />
          <div className="tokenFieldIcons">
            <button
              type="button"
              className="tokenFieldIcons__eye togglePassword cds--btn cds--btn--ghost cds--btn--icon-only"
              title={showPassword ? t('Hide Access Token') : t('Show Access Token')}
              aria-label={showPassword ? t('Hide Access Token') : t('Show Access Token')}
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <ViewOff size={16} /> : <View size={16} />}
            </button>
            <button
              type="button"
              className="tokenFieldIcons__copy cds--btn cds--btn--ghost cds--btn--icon-only"
              title={t('copy_to_clipboard')}
              aria-label={t('copy_to_clipboard')}
              onClick={() => navigator.clipboard.writeText(authToken.access_token)}
              data-testid="copy-button"
            >
              <Copy size={16} />
            </button>
          </div>
        </div>
      </div>
      {authError && authError.highlightedText && (
        <div className="authError">
          <pre>
            <code className="boundedSection hljs oauthError" dangerouslySetInnerHTML={authError.highlightedText} />
          </pre>
        </div>
      )}
    </>
  );
}
export default TestToolAccessToken;
